import { useSelector, useDispatch } from 'react-redux'
import { gql, useMutation } from '@apollo/client'
import { useCart } from 'lib/core/hooks/useCart'
import { DEFAULT_COUNTRY_CODE, CHECKOUT_SUBMIT_DELAY } from 'lib/core/resources/constants'
import { addressMatch, addressIsEmpty } from 'lib/core/resources/helpers'

// Types
import { CartData, CheckoutSchema } from 'lib/core/data/quote/cart/types'
import { ShippingAddressInput, BillingAddressInput, ShippingMethodInput, Callback } from 'lib/types'

// Selectors
import { getIsLoggedIn } from 'lib/core/data/auth/session/selectors'
import { getCheckoutConfig } from 'lib/core/data/quote/checkoutConfig/selectors'
import { getGuestEmail } from 'lib/core/data/quote/setGuestEmail/selectors'
import { getShippingAddress } from 'lib/core/data/quote/setShippingAddress/selectors'
import { getShippingMethod } from 'lib/core/data/quote/setShippingMethod/selectors'
import { getBillingAddress } from 'lib/core/data/quote/setBillingAddress/selectors'
import { getPaymentMethod } from 'lib/core/data/quote/paymentMethod/selectors'
import { getPlaceOrder } from 'lib/core/data/quote/placeOrderNext/selectors'
import { getPlaceOrder as getPlaceOrderRest } from 'lib/core/data/quote/placeOrder/selectors'
import { getIsCheckoutLocked } from 'lib/core/data/quote/lockCheckout/selectors'
import { getUseShippingAddress } from 'lib/core/data/quote/useShippingAddress/selectors'

// Actions
import { setGuestEmail as _setGuestEmail } from 'lib/core/data/quote/setGuestEmail'
import { setShippingAddress as _setShippingAddress } from 'lib/core/data/quote/setShippingAddress'
import { setShippingMethod as _setShippingMethod } from 'lib/core/data/quote/setShippingMethod'
import { setBillingAddress as _setBillingAddress } from 'lib/core/data/quote/setBillingAddress'
import { setPaymentMethod as _setPaymentMethod, initPaymentMethod as _initPaymentMethod } from 'lib/core/data/quote/paymentMethod'
import { setUseShippingAddress } from 'lib/core/data/quote/useShippingAddress'
import { placeOrder } from 'lib/core/data/quote/placeOrderNext'
import { setLockCheckout } from 'lib/core/data/quote/lockCheckout'
import { useCoreContext } from 'lib/core/hooks/useCoreContext'
import { useOfflinePaymentMethod } from '../useOfflinePaymentMethod'

export interface SetShippingAddressParams { // TODO: re use from data/quote/setShippingAddress/index.ts
  shippingAddress: ShippingAddressInput
  [key: string]: any
}

export interface SetBillingAddressParams { // TODO: re use from data/quote/setBillingAddress/index.ts
  address?: BillingAddressInput
  addressId?: number
  sameAsShipping?: boolean
  [key: string]: any
}

export interface SetBillingAddressSameAsShippingParams {
  value?: boolean
  customAddress?: BillingAddressInput
}

export const setPaymentMethodOnCartMutation = gql`
  mutation setPaymentMethodOnCart(
    $cartId: String!
    $code: String!
  ) {
    setPaymentMethodOnCart(
      input: {
        cart_id: $cartId,
        payment_method: {
          code: $code
        }
      }
    ) {
      cart {
        selected_payment_method {
          code
          title
        }
      }
    }
  }
`

export const useCheckout = () => {
  const dispatch = useDispatch()
  const { cartId, hasPhysicalProducts, cart } = useCart()
  const { magentoClient } = useCoreContext()
  const isLoggedIn = useSelector(getIsLoggedIn)
  const checkoutConfig = useSelector(getCheckoutConfig(cartId))
  const { isFetching: isPlacingOrderGraphQL } = useSelector(getPlaceOrder(cartId))
  const { isFetching: isPlacingOrderRest } = useSelector(getPlaceOrderRest(cartId))
  const isPlacingOrder = isPlacingOrderGraphQL || isPlacingOrderRest
  const checkoutSchema = getCheckoutSchema(cart?.data, isLoggedIn)
  const guestEmail = useSelector(getGuestEmail(cartId))
  const billingAddress = useSelector(getBillingAddress(cartId))
  const shippingAddress = useSelector(getShippingAddress(cartId))
  const shippingMethod = useSelector(getShippingMethod(cartId))
  const paymentMethod = useSelector(getPaymentMethod(cartId))
  const isCheckoutLocked = useSelector(getIsCheckoutLocked(cartId))
  const useShippingAddress = useSelector(getUseShippingAddress(cartId))
  const hasShippingMethods = Boolean(checkoutSchema?.shippingAddress?.availableShippingMethods.length)
  const orderTotal = cart?.data?.prices?.grandTotal?.value ?? 0
  const { setFreePaymentMethod } = useOfflinePaymentMethod()
  const [setPaymentMethodOnCart] = useMutation(setPaymentMethodOnCartMutation)

  /**
   * Validate Email
   */
  const validateEmail = () => {
    if (isLoggedIn) {
      return true
    }

    return !guestEmail.isFetching && !guestEmail.isFailure && checkoutSchema?.email
  }

  const addExtraDataOnCart = async (data: Record<string, string>) => {
    if (!cartId) {
      return
    }

    await magentoClient?.quote.setExtraDataOnCart({
      cartId,
      data
    })
  }

  /**
   * Validate Shipping Address
   */
  const validateShippingAddress = () => {
    if (!hasPhysicalProducts) {
      return true
    }

    return !shippingAddress.isFetching && !shippingAddress.isFailure && !addressIsEmpty(checkoutSchema?.shippingAddress)
  }

  /**
   * Validates the shipping method
   */
  const validateShippingMethod = () => {
    if (!hasPhysicalProducts) {
      return true
    }

    const { method_code: methodCode, carrier_code: carrierCode } = checkoutSchema?.shippingMethod ?? {}
    const shippingMethodIsEmpty = !methodCode || !carrierCode

    return !shippingMethod.isFetching && !shippingMethod.isFailure && !shippingMethodIsEmpty
  }

  /**
   * Validate Billing Address
   */
  const validateBillingAddress = () => {
    return !billingAddress.isFetching && !billingAddress.isFailure && !addressIsEmpty(checkoutSchema?.billingAddress)
  }

  return {
    cartId,
    checkoutSchema,
    checkoutConfig,
    defaultCountryCode: DEFAULT_COUNTRY_CODE,
    shippingAddress,
    shippingMethod,
    billingAddress,
    isPlacingOrder,
    isCheckoutLocked,
    paymentMethod, // TODO: type.
    orderTotal,
    useShippingAddress,
    hasShippingMethods,
    addExtraDataOnCart,

    /**
     * Validates the checkout schema
     */
    validateCheckout: () => {
      const isEmailValid = validateEmail()
      const isBillingAddressValid = validateBillingAddress()
      const isShippingAddressValid = validateShippingAddress()
      const isShippingMethodValid = validateShippingMethod()

      const hasPaymentMethod = Boolean(paymentMethod.code)
      const needsPaymentMethod = orderTotal > 0
      const isFreeOrder = orderTotal === 0

      return isEmailValid && isShippingAddressValid && isShippingMethodValid && isBillingAddressValid && (
        needsPaymentMethod ? hasPaymentMethod : isFreeOrder
      )
    },

    /**
     * Validates the email
     */
    validateEmail,

    /**
     * Check if shipping is needed
     */
    requiresShipping: (): boolean => {
      return hasPhysicalProducts
    },

    /**
     * Validates the shipping address
     */
    validateShippingAddress,

    /**
     * Validates the shipping method
     */
    validateShippingMethod,

    /**
     * Validates the billing address
     */
    validateBillingAddress,

    /**
     * Save guest email
     */
    setGuestEmail: (email: string, options?: CheckoutHookOptions) => {
      if (!cartId) {
        return
      }

      if (options?.debounce) {
        clearTimeout(guestEmailDebounce)
        guestEmailDebounce = setTimeout(() => {
          dispatch(_setGuestEmail(cartId, email, options?.callback))
        }, CHECKOUT_SUBMIT_DELAY)
      } else {
        dispatch(_setGuestEmail(cartId, email, options?.callback))
      }
    },

    /**
     * Save shipping address
     */
    setShippingAddress: ({ shippingAddress, ...restInputParams }: SetShippingAddressParams, options?: CheckoutHookOptions) => {
      if (!cartId) {
        return
      }

      if (options?.debounce) {
        clearTimeout(shippingAddressDebounce)
        shippingAddressDebounce = setTimeout(() => {
          dispatch(_setShippingAddress(cartId, { shippingAddress, ...restInputParams }, options?.callback))
        }, CHECKOUT_SUBMIT_DELAY)
      } else {
        dispatch(_setShippingAddress(cartId, { shippingAddress, ...restInputParams }, options?.callback))
      }
    },

    /**
     * Save shipping method
     */
    setShippingMethod: (shippingMethod: ShippingMethodInput, options?: CheckoutHookOptions) => { // TODO: ShippingMethodInput
      if (!cartId) {
        return
      }

      if (options?.debounce) {
        clearTimeout(shippingMethodDebounce)
        shippingMethodDebounce = setTimeout(() => {
          dispatch(_setShippingMethod(cartId, shippingMethod, options?.callback))
        }, CHECKOUT_SUBMIT_DELAY)
      } else {
        dispatch(_setShippingMethod(cartId, shippingMethod, options?.callback))
      }
    },

    /**
     * Save billing address
     */
    setBillingAddress: ({ address, addressId, sameAsShipping, ...restInputParams }: SetBillingAddressParams, options?: CheckoutHookOptions) => {
      if (!cartId) {
        return
      }

      if (options?.debounce) {
        clearTimeout(billingAddressDebounce)
        billingAddressDebounce = setTimeout(() => {
          dispatch(_setBillingAddress(cartId, { address, addressId, sameAsShipping, ...restInputParams }, options?.callback))
        }, CHECKOUT_SUBMIT_DELAY)
      } else {
        dispatch(_setBillingAddress(cartId, { address, addressId, sameAsShipping, ...restInputParams }, options?.callback))
      }
    },

    /**
     * * Save billing address same as shipping
     */
    setBillingAddressSameAsShipping: (params?: SetBillingAddressSameAsShippingParams, options?: CheckoutHookOptions) => {
      if (!cartId) {
        return
      }

      const value = params?.value
      const customAddress = params?.customAddress
      const shippingAddress = customAddress ?? checkoutSchema?.shippingAddress
      const hasEqualAddresses = addressMatch(shippingAddress, checkoutSchema?.billingAddress)

      dispatch(setUseShippingAddress({ cartId, value: value ?? hasEqualAddresses }))

      if (value && shippingAddress && !hasEqualAddresses) {
        dispatch(_setBillingAddress(cartId, { address: shippingAddress }, options?.callback))
      }
    },

    /**
     * Select payment method
     */
    setPaymentMethod: (code: string) => {
      if (!cartId) {
        return
      }

      dispatch(_setPaymentMethod({
        cartId,
        code
      }))
    },

    /**
     * Set payment method
     */
    setPaymentMethodAndPlaceOrder: (code: string) => {
      if (!cartId) {
        return
      }

      setPaymentMethodOnCart(
        {
          variables: {
            cartId,
            code
          }
        }).then(() => {
        dispatch(_setPaymentMethod({
          cartId,
          code
        }))

        dispatch(placeOrder(cartId))
      })
    },

    /**
     * Initialize payment method
     */
    initPaymentMethod: () => {
      if (!cartId) {
        return
      }

      if (orderTotal === 0) {
        setFreePaymentMethod().then(() => {
          dispatch(placeOrder(cartId))
        })
      } else {
        dispatch(_initPaymentMethod({ cartId }))
      }
    },

    /**
     * Prevents the user to modify the UI
     */
    lockCheckout: () => {
      if (!cartId) {
        return
      }

      dispatch(setLockCheckout({
        cartId,
        value: true
      }))
    },

    /**
     * Undo the lock checkout action
     */
    unlockCheckout: () => {
      if (!cartId) {
        return
      }

      dispatch(setLockCheckout({
        cartId,
        value: false
      }))
    },

    /**
     * Place the order
     */
    placeOrder: () => {
      if (!cartId) {
        return
      }

      dispatch(placeOrder(cartId))
    }
  }
}

export const getCheckoutSchema = (cart?: CartData, isLoggedIn?: boolean): CheckoutSchema | undefined => {
  if (!cart) {
    return
  }

  const {
    billingAddress,
    shippingAddress
  } = cart

  const {
    selectedShippingMethod
  } = shippingAddress ?? {}

  const schema: CheckoutSchema = {
    billingAddress,
    shippingAddress,
    shippingMethod: selectedShippingMethod
  }

  if (!isLoggedIn) {
    schema.email = cart.email
  }

  return schema
}

interface CheckoutHookOptions {
  debounce?: boolean
  callback?: Callback
}

let guestEmailDebounce: any
let shippingAddressDebounce: any
let shippingMethodDebounce: any
let billingAddressDebounce: any
